import { useState } from "react";
import { ShowBase, useShowContext, useTranslate } from "ra-core";
import { UserCheck } from "lucide-react";
import { EditButton } from "@/components/admin/edit-button";
import { ReferenceField } from "@/components/admin/reference-field";
import { DateField } from "@/components/admin/date-field";
import { TextField } from "@/components/admin/text-field";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { NoteCreate, NotesPanel } from "../notes";
import { ConvertLeadDialog } from "./ConvertLeadDialog";
import { LEAD_STATUS_CHOICES } from "./leadConsts";

export const LeadShow = () => {
  return (
    <ShowBase>
      <LeadShowContent />
    </ShowBase>
  );
};

const ShowField = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div>
    <p className="text-sm font-medium text-muted-foreground mb-1">{label}</p>
    <div className="text-sm">{children}</div>
  </div>
);

const LeadShowContent = () => {
  const { record, isPending } = useShowContext();
  const translate = useTranslate();
  const [convertOpen, setConvertOpen] = useState(false);

  if (isPending || !record) return null;

  const isConverted = !!record.converted_company_id;
  const statusLabel =
    LEAD_STATUS_CHOICES.find((s) => s.id === record.status)?.name ??
    record.status;
  const fullName =
    [record.first_name, record.last_name].filter(Boolean).join(" ") || "—";
  const emails = (record.email_jsonb ?? [])
    .map((e: any) => e?.email)
    .filter(Boolean);
  const phones = (record.phone_jsonb ?? [])
    .map((p: any) => p?.number)
    .filter(Boolean);

  return (
    <div className="mt-2 mb-12 flex flex-col gap-6 w-full">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">
            {fullName}
            {record.lead_code ? (
              <span className="text-muted-foreground font-normal">
                {" "}
                · {record.lead_code}
              </span>
            ) : null}
          </h1>
          <Badge variant="outline">{statusLabel}</Badge>
        </div>
        <div className="flex items-center gap-3">
          {!isConverted && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConvertOpen(true)}
            >
              <UserCheck className="h-4 w-4 mr-1" />
              {translate("resources.leads.convert.confirm", { _: "Convert" })}
            </Button>
          )}
          <EditButton />
        </div>
      </div>

      {isConverted && (
        <Card>
          <CardContent className="pt-4">
            <h2 className="text-base font-semibold mb-4">
              {translate("resources.leads.converted_header", {
                _: "Converted",
              })}
            </h2>
            <Separator className="mb-4" />
            <div className="grid grid-cols-2 gap-6">
              <ShowField
                label={translate("resources.leads.fields.converted_company_id", {
                  _: "Account",
                })}
              >
                <ReferenceField
                  source="converted_company_id"
                  reference="companies"
                  link="show"
                >
                  <TextField source="name" />
                </ReferenceField>
              </ShowField>
              <ShowField
                label={translate("resources.leads.fields.converted_contact_id", {
                  _: "Contact",
                })}
              >
                <ReferenceField
                  source="converted_contact_id"
                  reference="contacts"
                  link="show"
                >
                  <TextField source="first_name" />
                </ReferenceField>
              </ShowField>
              <ShowField
                label={translate("resources.leads.fields.converted_at", {
                  _: "Converted at",
                })}
              >
                <DateField source="converted_at" showTime />
              </ShowField>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="pt-4">
          <h2 className="text-base font-semibold mb-4">
            {translate("resources.leads.sections.overview", { _: "Overview" })}
          </h2>
          <Separator className="mb-4" />
          <div className="grid grid-cols-3 gap-6">
            <ShowField
              label={translate("resources.leads.fields.account_name", {
                _: "Account name",
              })}
            >
              {record.account_name || "—"}
            </ShowField>
            <ShowField
              label={translate("resources.leads.fields.lead_type", {
                _: "Lead type",
              })}
            >
              {record.lead_type || "—"}
            </ShowField>
            <ShowField
              label={translate("resources.leads.fields.lead_direction", {
                _: "Direction",
              })}
            >
              {record.lead_direction || "—"}
            </ShowField>
            <ShowField
              label={translate("resources.leads.fields.lead_source", {
                _: "Source",
              })}
            >
              {record.lead_source || "—"}
            </ShowField>
            <ShowField
              label={translate("resources.leads.fields.lead_channel", {
                _: "Channel",
              })}
            >
              {record.lead_channel || "—"}
            </ShowField>
            <ShowField
              label={translate("resources.leads.fields.first_contact_date", {
                _: "First contact",
              })}
            >
              <DateField source="first_contact_date" />
            </ShowField>
            <ShowField
              label={translate("resources.leads.fields.sales_id", {
                _: "Owner",
              })}
            >
              <ReferenceField source="sales_id" reference="sales" link="show">
                <TextField source="first_name" />
              </ReferenceField>
            </ShowField>
            {record.reason_for_dead && (
              <ShowField
                label={translate("resources.leads.fields.reason_for_dead", {
                  _: "Reason for dead",
                })}
              >
                {record.reason_for_dead}
              </ShowField>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-4">
          <h2 className="text-base font-semibold mb-4">
            {translate("resources.leads.sections.contact", {
              _: "Contact info",
            })}
          </h2>
          <Separator className="mb-4" />
          <div className="grid grid-cols-3 gap-6">
            <ShowField
              label={translate("resources.leads.fields.email", { _: "Email" })}
            >
              {emails.length ? emails.join(", ") : "—"}
            </ShowField>
            <ShowField
              label={translate("resources.leads.fields.phone_number", {
                _: "Phone",
              })}
            >
              {phones.length ? phones.join(", ") : "—"}
            </ShowField>
            <ShowField
              label={translate("resources.leads.fields.do_not_call", {
                _: "Do not call",
              })}
            >
              {record.do_not_call ? "Yes" : "No"}
            </ShowField>
            <ShowField
              label={translate("resources.leads.sections.address", {
                _: "Address",
              })}
            >
              {[
                record.address,
                record.city,
                record.state_abbr,
                record.zipcode,
                record.country,
              ]
                .filter(Boolean)
                .join(", ") || "—"}
            </ShowField>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-4">
          <h2 className="text-base font-semibold mb-4">
            {translate("resources.leads.sections.business", {
              _: "Business details",
            })}
          </h2>
          <Separator className="mb-4" />
          <div className="grid grid-cols-3 gap-6">
            <ShowField
              label={translate("resources.leads.fields.preferred_language", {
                _: "Language",
              })}
            >
              {record.preferred_language || "—"}
            </ShowField>
            <ShowField
              label={translate("resources.leads.fields.collection_method", {
                _: "Collection method",
              })}
            >
              {record.collection_method || "—"}
            </ShowField>
            <ShowField
              label={translate("resources.leads.fields.can_load", {
                _: "Can load",
              })}
            >
              {record.can_load || "—"}
            </ShowField>
            <ShowField
              label={translate("resources.leads.fields.loads_per_month", {
                _: "Loads per month",
              })}
            >
              {record.loads_per_month ?? "—"}
            </ShowField>
            <ShowField
              label={translate("resources.leads.fields.tax_id", {
                _: "Tax ID",
              })}
            >
              {record.tax_id || "—"}
            </ShowField>
            <ShowField
              label={translate("resources.leads.fields.competitor", {
                _: "Competitor",
              })}
            >
              {record.competitor || "—"}
            </ShowField>
            {record.competitor_notes && (
              <div className="col-span-3">
                <ShowField
                  label={translate(
                    "resources.leads.fields.competitor_notes",
                    { _: "Competitor notes" },
                  )}
                >
                  <p className="whitespace-pre-line">
                    {record.competitor_notes}
                  </p>
                </ShowField>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {(record.lead_notes || record.description || record.account_description) && (
        <Card>
          <CardContent className="pt-4">
            <h2 className="text-base font-semibold mb-4">
              {translate("resources.leads.sections.notes", { _: "Notes" })}
            </h2>
            <Separator className="mb-4" />
            <div className="flex flex-col gap-4">
              {record.lead_notes && (
                <ShowField
                  label={translate("resources.leads.fields.lead_notes", {
                    _: "Original inquiry",
                  })}
                >
                  <p className="whitespace-pre-line">{record.lead_notes}</p>
                </ShowField>
              )}
              {record.description && (
                <ShowField
                  label={translate("resources.leads.fields.description", {
                    _: "Description",
                  })}
                >
                  <p className="whitespace-pre-line">{record.description}</p>
                </ShowField>
              )}
              {record.account_description && (
                <ShowField
                  label={translate(
                    "resources.leads.fields.account_description",
                    { _: "Account description" },
                  )}
                >
                  <p className="whitespace-pre-line">
                    {record.account_description}
                  </p>
                </ShowField>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <NotesPanel
        resource="lead_notes"
        reference="leads"
        filter={{ lead_id: record.id }}
        title={translate("resources.leads.sections.activity", {
          _: "Activity",
        })}
        empty={<NoteCreate reference="leads" />}
      />

      <ConvertLeadDialog
        lead={record}
        open={convertOpen}
        onOpenChange={setConvertOpen}
      />
    </div>
  );
};
